import React, { useCallback, useEffect, useState, useRef } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
type Product = {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
};
// ---- Chilean buyer data ----
const NAMES = ['Camila Fuentes', 'Matías González', 'Javiera Rojas', 'Benjamín Muñoz', 'Antonia Díaz', 'Vicente Soto', 'Catalina Pérez', 'Sebastián Araya', 'Isidora Castro', 'Tomás Espinoza', 'Florencia Vergara', 'Joaquín Morales', 'Martina Contreras', 'Cristóbal Reyes', 'Valentina Tapia', 'Felipe Sepúlveda', 'Constanza Núñez', 'Ignacio Fuentealba', 'Emilia Cárdenas', 'Diego Saavedra', 'Trinidad Vega', 'Agustín Riquelme', 'Fernanda Bravo', 'Maximiliano Cortés'];
const CITIES = ['Santiago', 'Providencia', 'Las Condes', 'Maipú', 'Puente Alto', 'Viña del Mar', 'Valparaíso', 'Concepción', 'La Serena', 'Antofagasta', 'Temuco', 'Rancagua', 'Talca', 'Iquique', 'Puerto Montt', 'Chillán', 'Calama', 'Valdivia', 'Osorno', 'Quilpué'];
const FALLBACK_THUMB = 'https://cdn.magicpatterns.com/uploads/h84MHAz1wLnhee739csdrA/_vd-yBYkVV4GGjDCpwrs2rQ_-ulCWs0OSo-cuA9KvxGbUQ_cover.png';
const FALLBACK_PRODUCTS: Product[] = [{
  title: 'Código Calma',
  editionLabel: 'Programa Completo',
  priceNumber: 6390,
  priceText: '$6.390 CLP',
  thumbnailUrl: FALLBACK_THUMB
}];
// Format a number as Chilean pesos: $6.390 CLP
function formatCLP(n: number): string {
  return `$${n.toLocaleString('es-CL')} CLP`;
}
// Parse a CL price string like "$6.390" / "6.390 CLP" / "$6390" into a number.
function parseCLPrice(text: string): number | null {
  const match = text.match(/\$?\s?(\d{1,3}(?:[.\s]\d{3})+|\d+)/);
  if (!match) return null;
  const digits = match[1].replace(/[.\s]/g, '');
  const value = parseInt(digits, 10);
  return Number.isFinite(value) ? value : null;
}
// ---- Product scanner ----
function scanProducts(): Product[] {
  const found: Product[] = [];
  // 1) Structured data: JSON-LD Product / Offer
  try {
    const scripts = document.querySelectorAll('script[type="application/ld+json"]');
    scripts.forEach(s => {
      try {
        const data = JSON.parse(s.textContent || '');
        const nodes = Array.isArray(data) ? data : [data];
        nodes.forEach((node: any) => {
          if (node && node['@type'] === 'Product') {
            const offer = Array.isArray(node.offers) ? node.offers[0] : node.offers;
            const priceNumber = offer ? Number(offer.price) : NaN;
            if (node.name && Number.isFinite(priceNumber)) {
              found.push({
                title: String(node.name),
                editionLabel: 'Programa Completo',
                priceNumber,
                priceText: formatCLP(priceNumber),
                thumbnailUrl: Array.isArray(node.image) ? node.image[0] : node.image || FALLBACK_THUMB
              });
            }
          }
        });
      } catch {
        /* ignore malformed JSON-LD */
      }
    });
  } catch {
    /* ignore */
  }
  // 2) DOM scan: look for the "PRECIO HOY" price in the pricing section.
  if (found.length === 0) {
    try {
      const pricing = document.getElementById('pricing');
      if (pricing) {
        const text = pricing.textContent || '';
        // grab the "PRECIO HOY $X.XXX" value
        const m = text.match(/PRECIO HOY\s*\$?\s?([\d.\s]+)/i);
        const priceNumber = m ? parseCLPrice(m[1]) : parseCLPrice(text.split('PRECIO HOY')[1] || '');
        const img = document.querySelector<HTMLImageElement>('header img[alt*="Código"]')?.src || FALLBACK_THUMB;
        if (priceNumber) {
          found.push({
            title: 'Código Calma',
            editionLabel: 'Programa Completo',
            priceNumber,
            priceText: formatCLP(priceNumber),
            thumbnailUrl: img
          });
        }
      }
    } catch {
      /* ignore */
    }
  }
  return found.length > 0 ? found : FALLBACK_PRODUCTS;
}
// pick random element
function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
type ActiveToast = {
  id: number;
  name: string;
  city: string;
  product: Product;
};
export function PurchaseToast() {
  const prefersReducedMotion = useReducedMotion();
  const [catalog, setCatalog] = useState<Product[]>(FALLBACK_PRODUCTS);
  const [toast, setToast] = useState<ActiveToast | null>(null);
  const recentNames = useRef<string[]>([]);
  const idCounter = useRef(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Scan products once on mount
  useEffect(() => {
    // Defer slightly so the DOM (prices) is fully rendered
    const t = setTimeout(() => setCatalog(scanProducts()), 500);
    return () => clearTimeout(t);
  }, []);
  const pickName = useCallback((): string => {
    const history = recentNames.current;
    const available = NAMES.filter(n => !history.includes(n));
    const chosen = available.length > 0 ? pick(available) : pick(NAMES);
    history.push(chosen);
    if (history.length > 10) history.shift();
    return chosen;
  }, []);
  const showToast = useCallback(() => {
    idCounter.current += 1;
    const next: ActiveToast = {
      id: idCounter.current,
      name: pickName(),
      city: pick(CITIES),
      product: pick(catalog)
    };
    setToast(next);
    // Hide after 4 seconds
    const hideTimer = setTimeout(() => {
      setToast(cur => cur && cur.id === next.id ? null : cur);
    }, 4000);
    timers.current.push(hideTimer);
  }, [catalog, pickName]);
  // Schedule loop: first at 3s, then every 8–15s
  useEffect(() => {
    const scheduleNext = (delay: number) => {
      const t = setTimeout(() => {
        showToast();
        const nextDelay = 8000 + Math.random() * 7000; // 8–15s
        scheduleNext(nextDelay + 4000); // account for visible duration
      }, delay);
      timers.current.push(t);
    };
    scheduleNext(3000);
    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [showToast]);
  const enter = prefersReducedMotion ? {
    opacity: 1,
    y: 0
  } : {
    opacity: 1,
    y: 0
  };
  const initial = prefersReducedMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 40
  };
  const exit = prefersReducedMotion ? {
    opacity: 0,
    y: 0
  } : {
    opacity: 0,
    y: 40
  };
  return <div className="fixed bottom-4 inset-x-0 z-[120] flex justify-center px-4 pointer-events-none" data-id="element-190">
      <AnimatePresence data-id="element-191">
        {toast && <motion.div key={toast.id} initial={initial} animate={enter} exit={exit} transition={{
        duration: prefersReducedMotion ? 0.15 : 0.4,
        ease: 'easeOut'
      }} role="status" aria-live="polite" className="pointer-events-auto flex items-center gap-3 bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-100 p-3 pr-4 max-w-sm w-full" data-id="element-192">
            <img src={toast.product.thumbnailUrl} alt={toast.product.title} className="w-12 h-12 rounded-lg object-cover bg-navy-50 shrink-0" data-id="element-193" />
            <div className="min-w-0" data-id="element-194">
              <p className="text-sm text-navy leading-snug" data-id="element-195">
                <span className="font-semibold" data-id="element-196">{toast.name}</span>
                <span className="text-gray-500" data-id="element-197"> en {toast.city}</span>
                <br data-id="element-198" />
                acaba de comprar{' '}
                <span className="font-semibold" data-id="element-199">
                  {toast.product.editionLabel}
                </span>{' '}
                —{' '}
                <span className="text-orange font-bold" data-id="element-200">
                  {toast.product.priceText}
                </span>
              </p>
            </div>
            <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0 ml-1" aria-hidden="true" data-id="element-201" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}